import { useMemo } from 'react';
import { usePreviewReelsPlanQuery } from '../../../../../../redux/api/fymAPI';
import type { ReelsPlanPreview, ReelsPlanSettings } from '../../../../../../redux/api/fymAPI.types';
import { useDebouncedValue } from '../../../../../smartLinks/catalog/useDebouncedValue';
import type { SocialPlatform } from '../../../types';
import { REELS_PLAN_PREVIEW_DEBOUNCE_MS } from './reelsPlanModel';

interface UseReelsPlanPreviewProps {
  accountId: number;
  catalogItemUuid: string;
  settings: ReelsPlanSettings;
  /** The channels of the plan (the ticked chips). */
  platforms: SocialPlatform[];
  /** False for settings that the server refuses (a length outside 3 to 60, a start day before today). */
  isValid: boolean;
  /** True for the demo artist and a read-only artist: the dry run is a POST, and they send none. */
  skip: boolean;
}

export interface ReelsPlanPreviewState {
  /** The answer for the settings on the screen; undefined while it is checked. */
  preview: ReelsPlanPreview | undefined;
  /** A control changed and its answer is not here yet: "Make the plan" waits. */
  isChecking: boolean;
  /** The dry run failed (a 400, the network). */
  hasFailed: boolean;
}

/**
 * The dry run of the setup (design rollout-plan f1): each change of a control asks the server for
 * the days, the counts, the credits left and the reason that stops the plan, after 300 ms with no
 * other change. The first read has no wait.
 */
export function useReelsPlanPreview({
  accountId,
  catalogItemUuid,
  settings,
  platforms,
  isValid,
  skip,
}: UseReelsPlanPreviewProps): ReelsPlanPreviewState {
  const { startDay, days, cadence, postsPerDay } = settings;
  const args = useMemo(
    () => ({ accountId, catalogItemUuid, startDay, days, cadence, postsPerDay, platforms }),
    [accountId, cadence, catalogItemUuid, days, platforms, postsPerDay, startDay]
  );
  const debounced = useDebouncedValue(args, REELS_PLAN_PREVIEW_DEBOUNCE_MS);
  const isOff = skip || !isValid;
  const query = usePreviewReelsPlanQuery(debounced, { skip: isOff });
  const isSettled = debounced === args && !query.isFetching;

  if (isOff) {
    return { preview: undefined, isChecking: false, hasFailed: false };
  }
  return {
    // The answer of the settings before the change stays on the screen while the new one is read.
    preview: query.isError ? undefined : query.data,
    isChecking: !isSettled,
    hasFailed: isSettled && query.isError,
  };
}
